import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Anchor, Checkbox, Group, Table, Text, Tooltip } from '@mantine/core';
import Adjustments from './Adjustments';
import { getElementShortName } from '../../../../lib/formatters';
import { getFieldData, standardizeDecimals, formatValue } from '../utils';
import { IAppState } from '../../../../lib/types/AppState/index';
import { Column, INode, ISetMatchDataLoading } from '../Types/BulkPricingTypes';

export interface IDataCutProps {
  disableCheckbox?: (currentCut: INode) => boolean;
  isChecked: (dataCut: INode) => boolean;
  isSurveyPanel: boolean;
  job?: INode;
  onCheckDataCut: (currentCut: INode) => void;
  setMatchDataLoading?: ISetMatchDataLoading;
  surveyJob: INode;
  updateMatchesInState?: (updatedJob: INode) => void;
}

export default function DataCuts({
  disableCheckbox,
  isChecked,
  isSurveyPanel,
  job,
  onCheckDataCut,
  setMatchDataLoading,
  surveyJob,
  updateMatchesInState,
}: IDataCutProps) {
  const terms = surveyJob.terms;
  const organizationCode = useSelector((state: IAppState) => state.session.organization.code);
  const [isEditing, setIsEditing] = useState(false);

  const sortedDataCuts = useMemo(() => {
    const sorted = surveyJob.children || [];
    sorted.sort((a, b) => {
      const aName = getFieldData('job_scope_default', a.data, terms);
      const bName = getFieldData('job_scope_default', b.data, terms);
      return aName.localeCompare(bName);
    });
    return sorted;
  }, [surveyJob.children, terms]);

  const columns = useMemo(() => {
    const filteredColumns = terms.filter((header: Column) =>
      !header.term?.endsWith('_default') || header.term === 'job_scope_default'
    );
    filteredColumns.sort((a: Column, b: Column) =>
      a.term === 'job_scope_default' ? -1 : b.term === 'job_scope_default' ? 1 : 0
    );
    return filteredColumns.slice(0, 5);
  }, [terms]);

  const getSurveyJobDetailsPageHref = (dataCut: any[]) => {
    const cutId = getFieldData('cut_id_default', dataCut, terms);
    return `${window.location.origin}/view/o/${organizationCode}/mp/iframe/merit/merit-admin/survey_lib-3?one_cut_id=${cutId}`;
  };

  return (
    <div className="datacut__list">
      <Table withRowBorders={false} style={{ tableLayout: 'fixed', width: '100%' }} fz="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ textAlign: 'center', width: isSurveyPanel ? '20px' : '55px' }} />
            {columns.map((header: Column) => (
              <Table.Th style={{ textAlign: header.name ? 'center' : 'left', width: 'auto' }} key={header.term}>
                {header.name ? getElementShortName(header.name) : 'Data Cut'}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sortedDataCuts.map((dataCut: INode, idx: number) => {
            const aging = !isSurveyPanel ? standardizeDecimals(getFieldData('aging_factor_default', dataCut.data, terms)) : null;
            const currency = getFieldData('currency_default', dataCut.data, terms);
            const companies = getFieldData('total_companies_default', dataCut.data, terms) || '-';
            const incumbents = getFieldData('total_incumbents_default', dataCut.data, terms) || '-';

            return (
              <React.Fragment key={idx}>
                <Table.Tr
                  bg={isChecked(dataCut) ? 'rgba(162, 225, 252, 0.2)' : undefined}
                  onClick={() => onCheckDataCut(dataCut)}
                >
                  <Table.Td style={{ textAlign: 'center', width: isSurveyPanel ? '20px' : '55px' }}>
                    <Group align="center">
                      {!isSurveyPanel && (
                        <i
                          className="edit-adjustments icon-pencil"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsEditing(!isEditing);
                          }}
                        />
                      )}
                      <Tooltip
                        label={
                          disableCheckbox && disableCheckbox(dataCut)
                            ? 'Only data cuts that share a currency can be selected at the same time.'
                            : !isChecked(dataCut)
                            ? 'Select data cut'
                            : 'Deselect data cut'
                        }
                      >
                        <Checkbox
                          checked={isChecked(dataCut)}
                          color="skyblue"
                          disabled={disableCheckbox && disableCheckbox(dataCut)}
                          onChange={() => onCheckDataCut(dataCut)}
                          size="sm"
                        />
                      </Tooltip>
                    </Group>
                  </Table.Td>
                  {columns.map((header: Column) => {
                    const fieldData = formatValue(header, dataCut.data, terms) || '-';
                    const tooltipLabel = header.term === 'job_scope_default' 
                      ? `${fieldData}, ${companies} cos, ${incumbents} ees, ${currency}` 
                      : fieldData 
                    ;
                    const cellContents = header.term === 'job_scope_default' 
                      ?
                        (<Anchor
                          c="skyblue"
                          href={getSurveyJobDetailsPageHref(dataCut.data)}
                          onClick={(e) => e.stopPropagation()}
                          size="sm"
                          ta="right"
                          target="_blank"
                        >
                          {fieldData}
                        </Anchor>) 
                      : fieldData
                    ;

                    return (
                      <Table.Td style={{ textAlign: header.term === 'job_scope_default' ? 'left' : 'center', width: `${100 / (columns.length + 1)}%` }} key={header.term}>
                        <Tooltip label={tooltipLabel}>
                          <Text truncate='end' size='xs'>
                            {cellContents}
                          </Text>
                        </Tooltip>
                      </Table.Td>
                    )
                  })}
                </Table.Tr>
                {!isSurveyPanel && isEditing && (
                  <Adjustments
                    aging={aging}
                    columns={columns}
                    dataCut={dataCut}
                    job={job}
                    setIsEditing={setIsEditing}
                    setMatchDataLoading={setMatchDataLoading}
                    surveyJob={surveyJob}
                    updateMatchesInState={updateMatchesInState}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Table.Tbody>
      </Table>
    </div>
  );
}
